export const getDayAbbreviation = (dayName: string): string => {
  const dayMap: { [key: string]: string } = {
    Monday: 'Mon',
    Tuesday: 'Tue',
    Wednesday: 'Wed',
    Thursday: 'Thu',
    Friday: 'Fri',
    Saturday: 'Sat',
    Sunday: 'Sun',
  };
  return dayMap[dayName] || dayName.slice(0, 3);
};

export const getFullDayName = (dayAbbreviation: string): string => {
  const dayMap: { [key: string]: string } = {
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thu: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday',
    Sun: 'Sunday',
  };
  return dayMap[dayAbbreviation] || dayAbbreviation;
};

export const formatTime = (timeString: string) => {
  if (!timeString) return '';

  // If already in correct format (like "6:00PM"), return as is
  if (timeString.includes('AM') || timeString.includes('PM')) {
    return timeString;
  }

  // Convert 24-hour format (like "20:00") to 12-hour format
  const [hours, minutes] = timeString.split(':');
  const hour = Number.parseInt(hours, 10);
  const minute = minutes || '00';

  if (hour === 0) {
    return `12:${minute}AM`;
  } else if (hour < 12) {
    return `${hour}:${minute}AM`;
  } else if (hour === 12) {
    return `12:${minute}PM`;
  } else {
    return `${hour - 12}:${minute}PM`;
  }
};

export const formatStartDate = (dateString: string) => {
  if (!dateString) return '';

  // If already in correct format (like "12th May"), return as is
  if (
    dateString.includes('th') ||
    dateString.includes('st') ||
    dateString.includes('nd') ||
    dateString.includes('rd')
  ) {
    return dateString;
  }

  // Try to parse and format the date
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;

    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });

    const dayMod10 = day % 10;
    const dayMod100 = day % 100;
    let suffix = 'th';
    if (dayMod100 - dayMod10 !== 10 && dayMod10 <= 3) {
      const suffixes = ['th', 'st', 'nd', 'rd'];
      suffix = suffixes[dayMod10];
    }

    return `${day}${suffix} ${month}`;
  } catch {
    return dateString;
  }
};

import type {
  CourseWiseBatchesData,
  ProcessedCourseWiseBatchesData,
  BatchData,
  ProcessedBatch,
} from '../types';

// Data processing utilities
export const processSessionDetails = (schedule?: {
  session_details?: Array<{ day?: string; time?: string }>;
}) => {
  const sessionDetails = Array.isArray(schedule?.session_details)
    ? schedule.session_details
    : [];

  return sessionDetails.map(session => ({
    day: getDayAbbreviation(session.day ?? ''),
    time: formatTime(session.time ?? ''),
  }));
};

export const processBatch = (batch: BatchData): ProcessedBatch => {
  const { schedule } = batch;
  const batchTags = Array.isArray(batch.tags) ? batch.tags : [];

  return {
    batch_id: String(batch.batch_id ?? ''),
    scheduleDetails: processSessionDetails(schedule),
    startDate: formatStartDate(
      batch.start_date_formatted ?? schedule?.start_date ?? ''
    ),
    tags: batchTags,
    tentative: batch?.batch_status === 'dormant',
  };
};

export const processCourseWiseBatchesData = (
  courseWiseBatchesData: CourseWiseBatchesData
): ProcessedCourseWiseBatchesData => {
  const processedData: ProcessedCourseWiseBatchesData = {};

  for (const courseName of Object.keys(courseWiseBatchesData)) {
    const courseData = courseWiseBatchesData[courseName];
    if (!courseData) continue;

    processedData[courseName] = {
      recommended_batches: courseData.recommended_batches.map(
        (batch: BatchData) => processBatch(batch)
      ),
      other_batches: courseData.other_batches.map((batch: BatchData) =>
        processBatch(batch)
      ),
      status: courseData?.status,
    };
  }

  return processedData;
};

// Helper function to add bidirectional day combinations
const addBidirectionalCombination = (
  combinations: Record<string, Set<string>>,
  day1: string,
  day2: string
): void => {
  if (!combinations[day1]) combinations[day1] = new Set();
  combinations[day1].add(day2);

  if (!combinations[day2]) combinations[day2] = new Set();
  combinations[day2].add(day1);
};

// Helper function to process a single batch and update collections
const processBatchDays = (
  batch: ProcessedBatch,
  allDays: Set<string>,
  combinations: Record<string, Set<string>>
): void => {
  const batchDays = batch.scheduleDetails.map(schedule => schedule.day);

  // Add each day to allDays set
  for (const day of batchDays) {
    allDays.add(day);
  }

  // For batches with exactly 2 days, create bidirectional combinations
  if (batchDays.length === 2) {
    const [day1, day2] = batchDays;
    addBidirectionalCombination(combinations, day1, day2);
  }
};

// Helper function to process all batches for a course
const processCourseBatches = (
  courseData: ProcessedCourseWiseBatchesData[string],
  allDays: Set<string>,
  combinations: Record<string, Set<string>>
): void => {
  const allBatches = [
    ...courseData.recommended_batches,
    ...courseData.other_batches,
  ];

  for (const batch of allBatches) {
    processBatchDays(batch, allDays, combinations);
  }
};

// Utility to extract day combinations from processed batch data
export const extractDayCombinations = (
  processedBatchesData: ProcessedCourseWiseBatchesData
): Record<string, string[]> => {
  const combinations: Record<string, Set<string>> = {};
  const allDays = new Set<string>();

  // Extract all days and combinations from all batches
  for (const courseData of Object.values(processedBatchesData)) {
    if (courseData) {
      processCourseBatches(courseData, allDays, combinations);
    }
  }

  // Convert Sets to arrays and ensure all days are present in the map
  const result: Record<string, string[]> = {};
  for (const day of allDays) {
    result[day] = combinations[day] ? [...combinations[day]] : [];
  }

  return result;
};
